import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TireConfigurator from '../components/Tires/TireConfigurator';
import TireProcessSteps from '../components/Tires/TireProcessSteps';
import TireBrandsSection from '../components/Tires/TireBrandsSection';
import TireAdviceSection from '../components/Tires/TireAdviceSection';
import TireQuoteModal from '../components/Tires/TireQuoteModal';

interface TireConfig {
  type: string;
  brand: string;
  season: string;
  quantity: string;
  width: string;
  height: string;
  diameter: string;
  loadIndex: string;
  speedIndex: string;
  runflat: boolean;
  xl: boolean;
  reseller: boolean;
}

const TirePage: React.FC = () => {
  const { t } = useLanguage();
  const [config, setConfig] = useState<TireConfig>({
    type: 'tourisme',
    brand: 'indifferent',
    season: 'ete',
    quantity: '4',
    width: '',
    height: '',
    diameter: '',
    loadIndex: '',
    speedIndex: '',
    runflat: false,
    xl: false,
    reseller: false
  });
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const handleConfigChange = (key: keyof TireConfig, value: string | boolean) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const openQuoteModal = () => {
    setShowQuoteModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <div className="pt-24 min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #E30613 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, #E30613 0%, transparent 50%)`
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className="inline-block mb-6"
            >
              <div className="w-20 h-20 bg-gradient-to-r from-red-600 to-red-500 rounded-full flex items-center justify-center shadow-2xl">
                <Settings className="w-10 h-10 text-white" />
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">
                {t('tires.title')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              {t('tires.subtitle')}
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Main Components */}
      <TireConfigurator 
        config={config} 
        onConfigChange={handleConfigChange}
        onRequestQuote={openQuoteModal}
      />
      <TireProcessSteps />
      <TireBrandsSection />
      <TireAdviceSection />

      {/* Quote Modal */}
      <TireQuoteModal 
        isOpen={showQuoteModal}
        onClose={closeQuoteModal}
        config={config}
      />
    </div>
  );
};

export default TirePage;